import { useCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { getNetworkSearch, getStorageList } from "tm-shared/api";
import { PointType } from "tm-shared/types";
import { validationMessage } from "tm-shared/utils";

import { useAsyncOptions } from "@/hooks";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { RHFAutocomplete } from "@/shared/forms/RHF/RHFAutocomplete";
import {
    setPointCreatePageModal,
    setPointCreatePageOptionsCache,
} from "@/store/slices/ui";
import { NewPointFormValues } from "@/types/form";
import { PointCreateModal } from "@/types/modals";

import { FieldName } from "../CreatePointForm.constants";

interface PointServiceSectionProps {
    activeMap: Record<FieldName, boolean>;
    resetBelow: (field: FieldName) => void;
}

export const PointServiceSection = ({
    activeMap,
    resetBelow,
}: PointServiceSectionProps) => {
    // Form values
    const { setValue } = useFormContext<NewPointFormValues>();
    const dispatch = useAppDispatch();

    const region = useWatch<NewPointFormValues, "region">({ name: "region" });
    const network = useWatch<NewPointFormValues, "network">({
        name: "network",
    });
    const storage = useWatch<NewPointFormValues, "storage">({
        name: "storage",
    });
    const pointType = useWatch<NewPointFormValues, "pointType">({
        name: "pointType",
    });
    const partner = useWatch<NewPointFormValues, "partner">({
        name: "partner",
    });

    // Store data
    const { optionCache } = useAppSelector((s) => s.ui.pointCreatePage);

    // Handlers
    const handleAddNetwork = useCallback(() => {
        dispatch(
            setPointCreatePageModal({
                type: PointCreateModal.AddNetwork,
            }),
        );
    }, [dispatch]);

    // Storage options
    const { options: newStorageOptions, loading: loadingStorage } =
        useAsyncOptions({
            fetchFn: (signal) =>
                getStorageList(
                    {
                        partnerId: partner,
                        mainRegionId: region,
                    },
                    {
                        axiosOverrides: { signal },
                    },
                ),
            selectData: (response) => response.result,
            mapToOption: (s) => ({ label: s.name, value: s.id }),
            dependencies: [region, partner],
            enabled: activeMap.storage && !storage,
        });

    useEffect(() => {
        if (newStorageOptions?.length) {
            dispatch(
                setPointCreatePageOptionsCache({
                    fieldName: "storage",
                    options: newStorageOptions,
                }),
            );
        }
    }, [dispatch, newStorageOptions]);

    // Network options
    const [searchQueryChainName, setSearchQueryChainName] = useState("");

    const { options: newChainNameOptions, loading: loadingChainName } =
        useAsyncOptions({
            fetchFn: (signal) =>
                getNetworkSearch(
                    {
                        query: searchQueryChainName,
                    },
                    { axiosOverrides: { signal } },
                ),
            selectData: (response) => response.result,
            mapToOption: (s) => ({ label: s.name, value: s.id }),
            dependencies: [searchQueryChainName],
            enabled:
                ((!searchQueryChainName && !network) ||
                    searchQueryChainName.length >= 3) &&
                activeMap.network,
            debounceMs: 500,
        });

    useEffect(() => {
        if (newChainNameOptions?.length) {
            dispatch(
                setPointCreatePageOptionsCache({
                    fieldName: "network",
                    options: newChainNameOptions,
                }),
            );
        }
    }, [dispatch, newChainNameOptions]);

    const handleInputChange = useCallback(
        (value: string) => {
            setSearchQueryChainName(value);
            setValue("network", "");
        },
        [setValue],
    );

    // Render guards
    if (!pointType) {
        return null;
    }

    // Render
    return pointType === PointType.Chains ? (
        <RHFAutocomplete
            name="network"
            options={optionCache?.network ?? []}
            label="Назва мережі"
            allowClear={false}
            loading={loadingChainName}
            onInputChange={handleInputChange}
            rules={{
                required: validationMessage.required,
            }}
            disabled={!activeMap.network}
            actionOption={{
                label: "Додати мережу",
                onClick: handleAddNetwork,
            }}
            selectProps={{
                size: "large",
            }}
        />
    ) : (
        <RHFAutocomplete
            name="storage"
            options={optionCache?.storage ?? []}
            label="Обслуговується зі складу"
            disabled={!activeMap.storage}
            onAfterChange={() => resetBelow("storage")}
            allowClear={false}
            loading={loadingStorage}
            rules={{
                required: validationMessage.required,
            }}
            selectProps={{
                size: "large",
            }}
        />
    );
};
